import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
//@ts-ignore
import BpmnViewer from "bpmn-js/lib/NavigatedViewer";
import { useNavigate } from "react-router-dom";
import { Alert, Box, Snackbar, Stack, Link as MUILink, useMediaQuery } from "@mui/material";
import { Link } from "react-router-dom";
import { Group as PanelGroup, Panel, Separator as PanelResizeHandle } from "react-resizable-panels";
import { processmodelsApi } from "api";
import { getTranslation } from "common";
import { clearLanes } from "common/utilities";
import InputPopUp from "components/dialogs/InputPopUp";
import {
  useSelectedOrganization,
  useSnackbar,
  useTranslations,
  useTree,
  useUser,
} from "hooks";
import {
  ProcessModelFolderType,
  RevisionModelType,
  SelectedProcessModelType,
} from "model/processModel";
import { bpmnDiagramThemeSx } from "utils/bpmnDiagramStyles";
import dayjs from "dayjs";
import NewFolderModelPopUp from "../../components/NewFolderModelPopUp";
import DeleteFolderPopUpTypes from "../../components/DeleteFolderPopUp";
import ProcessBreadcrumbs, { ProcessBreadcrumbItem } from "./ProcessBreadcrumbs";
import ProcessLibraryTable, { ProcessLibraryRow } from "./ProcessLibraryTable";
import ProcessLibraryToolbar from "./ProcessLibraryToolbar";
import ProcessPreviewPanel from "./ProcessPreviewPanel";

const isFolder = (
  item: ProcessModelFolderType | SelectedProcessModelType
): item is ProcessModelFolderType => Object.prototype.hasOwnProperty.call(item, "children");

const getSortStorageKeys = (userId?: number, organizationId?: number) => {
  if (!userId || !organizationId) {
    return null;
  }
  return {
    name: `process-library-sort-name-${userId}-${organizationId}`,
    modified: `process-library-sort-modified-${userId}-${organizationId}`,
  };
};

const parseSortDirection = (value: string | null) =>
  value === "asc" || value === "desc" ? value : null;

const ProcessLibraryPage: React.FC = () => {
  const navigate = useNavigate();
  const translations = useTranslations();
  const user = useUser();
  const selectedOrganization = useSelectedOrganization();
  const { sendSnack } = useSnackbar();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const viewerRef = useRef<any>(null);
  const [currentFolderId, setCurrentFolderId] = useState<number | null>(null);
  const [selectedRow, setSelectedRow] = useState<ProcessLibraryRow | null>(null);
  const [searchText, setSearchText] = useState("");
  const [searchIndex, setSearchIndex] = useState(0);
  const [sortBy, setSortBy] = useState<"name" | "modified" | "none">("name");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [showNewFolderModel, setShowNewFolderModel] = useState(false);
  const [deleteFolderPopUp, setDeleteFolderPopUp] = useState(false);
  const [fileContent, setFileContent] = useState("");
  const [nameModelPopUp, setNameModelPopUp] = useState(false);
  const [errorModel, setErrorModel] = useState<SelectedProcessModelType | null>(null);
  const [errorInModel, setErrorInModel] = useState(false);
  const [currentModelXml, setCurrentModelXml] = useState("");
  const [revisionList, setRevisionList] = useState<RevisionModelType[]>([]);
  const [selectedRevision, setSelectedRevision] = useState<RevisionModelType | null>(null);
  const [initializedSortStorage, setInitializedSortStorage] = useState<string | null>(null);

  const {
    plain,
    processModelList,
    processModelFolders,
    setSelectedNode,
    getTreeNodes,
    createNewNode,
    deleteNode,
    updateTree,
    loading: treeLoading,
  } = useTree();

  // ponytail: useTree's plainTree skips the synthetic root (id: 0) and
  // with it all top-level folders, so folderById() / breadcrumbs can't
  // resolve them. Rebuild the flat list here by prepending the root's
  // real children, scoped to this page only.
  const plainWithRoot = useMemo(() => {
    const root = processModelFolders[0];
    if (!root || root.id !== 0 || !root.children?.length) {
      return plain;
    }
    return [...root.children, ...plain];
  }, [plain, processModelFolders]);

  const folderById = useCallback(
    (id: number | null) =>
      id === null
        ? null
        : (plainWithRoot.find((item) => isFolder(item) && item.id === id) as ProcessModelFolderType | undefined) || null,
    [plainWithRoot]
  );

  const childFolders = useMemo(() => {
    if (!processModelFolders.length) {
      return [];
    }
    if (currentFolderId === null) {
      return processModelFolders[0]?.children || [];
    }
    return folderById(currentFolderId)?.children || [];
  }, [currentFolderId, folderById, processModelFolders]);

  // ponytail: processReducer injects a synthetic { id: NaN } model as a
  // drag-target marker for the BPMN editor. It's not a real row — drop
  // it here so the library table doesn't render a nameless, untyped row.
  const childModels = useMemo(
    () =>
      processModelList.filter((model) => {
        if (!Number.isFinite(model.id)) {
          return false;
        }
        return currentFolderId === null
          ? !model.idfolder
          : model.idfolder === currentFolderId;
      }),
    [currentFolderId, processModelList]
  );

  const rows = useMemo<ProcessLibraryRow[]>(() => {
    const nextRows: ProcessLibraryRow[] = [
      ...childFolders.map((folder) => ({ kind: "folder" as const, folder })),
      ...childModels.map((model) => ({ kind: "model" as const, model })),
    ];
    const filtered = nextRows.filter((row) => {
      const name = row.kind === "folder" ? row.folder.name || "" : row.model.name || "";
      return name.toLowerCase().includes(searchText.toLowerCase());
    });
    if (sortBy === "none") {
      return filtered;
    }
    const dir = sortDir === "asc" ? 1 : -1;
    const compareName = (a: ProcessLibraryRow, b: ProcessLibraryRow) => {
      if (a.kind !== b.kind) return a.kind === "folder" ? -1 : 1;
      const nameA = a.kind === "folder" ? a.folder.name || "" : a.model.name || "";
      const nameB = b.kind === "folder" ? b.folder.name || "" : b.model.name || "";
      return dir * nameA.localeCompare(nameB);
    };
    const compareModified = (a: ProcessLibraryRow, b: ProcessLibraryRow) => {
      // Folders have no creation date — push them to the end in both directions.
      if (a.kind === "folder" && b.kind === "folder") return 0;
      if (a.kind === "folder") return 1;
      if (b.kind === "folder") return -1;
      const timeA = dayjs(a.model.creation).valueOf();
      const timeB = dayjs(b.model.creation).valueOf();
      return dir * (timeA - timeB);
    };
    return filtered.sort(sortBy === "name" ? compareName : compareModified);
  }, [childFolders, childModels, searchText, sortBy, sortDir]);

  const selectedFolderChildren = useMemo(
    () => (selectedRow?.kind === "folder" ? selectedRow.folder.children || [] : []),
    [selectedRow]
  );
  const selectedFolderModels = useMemo(
    () =>
      selectedRow?.kind === "folder"
        ? processModelList.filter((model) => model.idfolder === selectedRow.folder.id)
        : [],
    [processModelList, selectedRow]
  );

  const breadcrumbs = useMemo<ProcessBreadcrumbItem[]>(() => {
    const trail: ProcessBreadcrumbItem[] = [{ id: null, name: getTranslation(translations, "process_library.root") }];
    let folder = folderById(currentFolderId);
    const parents: ProcessBreadcrumbItem[] = [];
    while (folder) {
      parents.unshift({ id: folder.id, name: folder.name });
      folder = folderById(folder.idfolder || null);
    }
    return trail.concat(parents);
  }, [currentFolderId, folderById, translations]);

  const sortStorageKeys = useMemo(
    () => getSortStorageKeys(user?.userId, selectedOrganization?.id),
    [selectedOrganization?.id, user?.userId]
  );

  useEffect(() => {
    if (!sortStorageKeys) {
      setInitializedSortStorage(null);
      return;
    }
    const nameSort = parseSortDirection(localStorage.getItem(sortStorageKeys.name));
    const modifiedSort = parseSortDirection(localStorage.getItem(sortStorageKeys.modified));
    if (nameSort) {
      setSortBy("name");
      setSortDir(nameSort);
    } else if (modifiedSort) {
      setSortBy("modified");
      setSortDir(modifiedSort);
    } else {
      setSortBy("name");
      setSortDir("asc");
    }
    setInitializedSortStorage(sortStorageKeys.name);
  }, [sortStorageKeys]);

  useEffect(() => {
    if (!sortStorageKeys || initializedSortStorage !== sortStorageKeys.name) {
      return;
    }
    localStorage.setItem(sortStorageKeys.name, sortBy === "name" ? sortDir : "none");
    localStorage.setItem(sortStorageKeys.modified, sortBy === "modified" ? sortDir : "none");
  }, [initializedSortStorage, sortBy, sortDir, sortStorageKeys]);

  const openDiagram = useCallback(async (xml: string) => {
    const canvas = document.getElementById("previewer-canvas");
    if (!canvas) {
      return;
    }
    canvas.innerHTML = "";
    if (viewerRef.current) {
      viewerRef.current.destroy();
    }
    viewerRef.current = new BpmnViewer({
      container: canvas,
      readOnly: true,
      selectable: false,
      additionalModules: [],
    });
    try {
      await viewerRef.current.importXML(xml);
      const canvasApi: any = viewerRef.current.get("canvas");
      canvasApi.zoom("fit-viewport");
    } catch (error) {
      console.error(error);
    }
  }, []);

  useEffect(() => {
    if (selectedOrganization?.id) {
      getTreeNodes();
      setCurrentFolderId(null);
      setSelectedRow(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrganization?.id]);

  useEffect(() => {
    setSearchIndex(0);
  }, [currentFolderId, searchText]);

  useEffect(() => {
    if (selectedRow?.kind !== "model") {
      if (viewerRef.current) {
        viewerRef.current.destroy();
        viewerRef.current = null;
      }
      const canvas = document.getElementById("previewer-canvas");
      if (canvas) {
        canvas.innerHTML = "";
      }
      setCurrentModelXml("");
      setRevisionList([]);
      setSelectedRevision(null);
      return;
    }
    (async () => {
      const { data } = await processmodelsApi.get(`/${selectedRow.model.id}`);
      setCurrentModelXml(data.model || "");
      setSelectedRevision(null);
      if (data.model) {
        openDiagram(data.model);
      }

      try {
        const historyResponse = await processmodelsApi.get(`/${selectedRow.model.id}/histories`);
        const sortedRevisions = [...(historyResponse.data.processmodelshistories || [])].sort(
          (a: RevisionModelType, b: RevisionModelType) =>
            new Date(b.creation_time).getTime() - new Date(a.creation_time).getTime()
        );
        setRevisionList(sortedRevisions);
      } catch {
        setRevisionList([]);
      }
    })();
  }, [openDiagram, selectedRow]);

  const selectCurrentVersion = () => {
    setSelectedRevision(null);
    if (currentModelXml) {
      openDiagram(currentModelXml);
    }
  };

  const selectRevision = (revision: RevisionModelType) => {
    setSelectedRevision(revision);
    if (revision.model) {
      openDiagram(revision.model);
    }
  };

  const restoreRevision = async (revision: RevisionModelType) => {
    if (selectedRow?.kind !== "model" || selectedRow.model.modeltype) {
      return;
    }
    try {
      const { data } = await processmodelsApi.put(
        `/${selectedRow.model.id}/histories/${revision.id}/revert`,
        {}
      );
      sendSnack({ message: data.responsemessage, type: "success" });
      setSelectedRevision(null);
      setSelectedRow({
        kind: "model",
        model: { ...selectedRow.model, modeltype: revision.modeltype },
      });
      getTreeNodes();
    } catch (error: any) {
      sendSnack({ message: error.response?.data?.message, type: "error" });
    }
  };

  const createFolder = (folderName: string) => {
    createNewNode(folderName, (data) => {
      setShowNewFolderModel(false);
      sendSnack({ message: data.responsemessage, type: "success" });
    });
  };

  const importModel = async (nameModel: string) => {
    if (!fileContent) {
      sendSnack({
        message: getTranslation(translations, "generic.no_file_selected.caption"),
        type: "warning",
      });
      return;
    }
    const { data } = await processmodelsApi.post("", {
      model: fileContent,
      idorganization: selectedOrganization?.id,
      idfolder: currentFolderId,
      name: nameModel,
    });
    setNameModelPopUp(false);
    setFileContent("");
    sendSnack({ message: data.responsemessage, type: "success" });
    getTreeNodes();
  };

  const exportModel = async (model: SelectedProcessModelType) => {
    const { data } = await processmodelsApi.get(`${model.id}`);
    const element = document.createElement("a");
    element.setAttribute(
      "href",
      `data:text/plain;charset=utf-8,${encodeURIComponent(clearLanes(data.model))}`
    );
    element.setAttribute("download", `${model.name}.flw`);
    element.style.display = "none";
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
    sendSnack({ message: data.responsemessage, type: "success" });
  };

  const onFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) {
      return;
    }
    const reader = new FileReader();
    reader.onload = ({ target }) => {
      setFileContent(String(target?.result || ""));
      setNameModelPopUp(true);
      event.target.value = "";
    };
    reader.readAsText(selectedFile);
  };

  const openFolder = (folderId: number | null) => {
    setCurrentFolderId(folderId);
    setSelectedRow(null);
  };

  const nextSearchResult = () => {
    if (!rows.length) {
      return;
    }
    const nextIndex = searchIndex + 1 >= rows.length ? 0 : searchIndex + 1;
    setSearchIndex(nextIndex);
    setSelectedRow(rows[nextIndex]);
  };

  const previousSearchResult = () => {
    if (!rows.length) {
      return;
    }
    const nextIndex = searchIndex - 1 < 0 ? rows.length - 1 : searchIndex - 1;
    setSearchIndex(nextIndex);
    setSelectedRow(rows[nextIndex]);
  };

  const orderState = {
    name: { active: sortBy === "name", direction: sortBy === "name" ? sortDir : null },
    modified: { active: sortBy === "modified", direction: sortBy === "modified" ? sortDir : null },
  } as const;
  const zoomPreview = useCallback((ratio: number) => {
    const canvas = viewerRef.current?.get("canvas");
    if (canvas) {
      canvas.zoom(canvas.zoom() * ratio);
    }
  }, []);
  const isDesktop = useMediaQuery("(min-width:1200px)");

  const modelsTable = (
    <ProcessLibraryTable
      rows={rows}
      selectedRow={selectedRow}
      isAdmin={selectedOrganization?.administrator}
      loading={treeLoading}
      onOpenFolder={openFolder}
      onSelectRow={setSelectedRow}
      onEditModel={(modelId) => navigate(`/dashboard/process/edit/${modelId}/`)}
      onExportModel={exportModel}
      onDeleteFolder={(folderId) => {
        setSelectedNode(folderId);
        setDeleteFolderPopUp(true);
      }}
      order={orderState}
      onToggleOrder={(column) => {
        if (sortBy !== column) {
          setSortBy(column);
          setSortDir("asc");
          return;
        }
        if (sortDir === "asc") {
          setSortDir("desc");
        } else {
          setSortBy("none");
        }
      }}
      processModelFolders={processModelFolders[0]?.children ?? []}
      onMove={({ row, targetFolderId }) => {
        if (row.kind === "model") {
          if (targetFolderId === null) {
            if (row.model.idfolder) {
              updateTree({
                parentId: row.model.idfolder,
                childId: String(row.model.id),
                isProcessModel: true,
                deleteProcessFromFolder: true,
              });
            }
          } else {
            updateTree({
              parentId: targetFolderId,
              childId: String(row.model.id),
              childName: row.model.name,
              isProcessModel: true,
            });
          }
        } else {
          updateTree({
            parentId: targetFolderId,
            childId: String(row.folder.id),
            childName: row.folder.name,
            isProcessModel: false,
          });
        }
      }}
    />
  );

  const preview = (
    <ProcessPreviewPanel
      selectedRow={selectedRow}
      revisionList={revisionList}
      selectedRevision={selectedRevision}
      onSelectRevision={selectRevision}
      onSelectCurrentVersion={selectCurrentVersion}
      onRestoreRevision={restoreRevision}
      onZoom={zoomPreview}
      isAdmin={selectedOrganization?.administrator}
      folderChildren={selectedFolderChildren}
      folderModels={selectedFolderModels}
      modelXml={selectedRevision?.model || currentModelXml}
    />
  );

  return (
    <Box
      sx={{
        height: "100%",
        boxSizing: "border-box",
        overflow: "hidden",
        p: { xs: 2, lg: 3 },
      }}
    >
      <Stack spacing={2} height="100%" minHeight={0}>
        <ProcessLibraryToolbar
          isAdmin={selectedOrganization?.administrator}
          searchText={searchText}
          searchCount={rows.length}
          searchIndex={searchIndex}
          onSearchChange={setSearchText}
          onPreviousSearchResult={previousSearchResult}
          onNextSearchResult={nextSearchResult}
          onCreateModel={() => navigate("/dashboard/process/insert", { state: { folderId: currentFolderId } })}
          onCreateFolder={() => {
            setSelectedNode(currentFolderId);
            setShowNewFolderModel(true);
          }}
          onImportModel={() => fileInputRef.current?.click()}
        />

        <ProcessBreadcrumbs items={breadcrumbs} onNavigate={openFolder} />

        {isDesktop ? (
          <PanelGroup orientation="horizontal" style={{ flex: 1, minHeight: 0 }}>
            <Panel id="process-library-models" defaultSize="56%" minSize="30%" style={{ display: "flex", minWidth: 0 }}>
              {modelsTable}
            </Panel>
            <PanelResizeHandle
              aria-label="Ridimensiona anteprima"
              style={{ width: 10, cursor: "col-resize", flexShrink: 0, display: "flex", justifyContent: "center" }}
            >
              <Box sx={{ width: 1, height: "100%", bgcolor: "divider" }} />
            </PanelResizeHandle>
            <Panel id="process-library-preview" defaultSize="44%" minSize="30%" style={{ display: "flex", minWidth: 0 }}>
              <Box flex={1} minWidth={0} minHeight={0} sx={bpmnDiagramThemeSx}>{preview}</Box>
            </Panel>
          </PanelGroup>
        ) : (
          <Stack spacing={2} flex={1} minHeight={0}>
            <Box flex={1.15} minWidth={0} minHeight={0} display="flex">{modelsTable}</Box>
            <Box flex={0.9} minWidth={0} minHeight={0} sx={bpmnDiagramThemeSx}>{preview}</Box>
          </Stack>
        )}
      </Stack>

      <input
        ref={fileInputRef}
        type="file"
        hidden
        accept=".flw,.txt"
        onChange={onFileChange}
      />
      <NewFolderModelPopUp
        open={showNewFolderModel}
        setClose={setShowNewFolderModel}
        handleSubmit={createFolder}
      />
      <DeleteFolderPopUpTypes
        open={deleteFolderPopUp}
        setClose={setDeleteFolderPopUp}
        handleSubmit={() =>
          deleteNode(() => {
            setDeleteFolderPopUp(false);
            getTreeNodes();
          })
        }
      />
      <InputPopUp
        labels={{
          title: getTranslation(translations, "generic.model_name.caption"),
          inputLabel: getTranslation(translations, "generic.insert_model_name.caption"),
        }}
        callback={importModel}
        open={nameModelPopUp}
        onClose={() => setNameModelPopUp(false)}
      />
      <Snackbar
        anchorOrigin={{ horizontal: "center", vertical: "bottom" }}
        open={Boolean(errorModel && errorInModel)}
      >
        <MUILink
          sx={{ textDecoration: "none" }}
          component={Link}
          to={`/dashboard/process/edit/${errorModel?.id}/?validate=1`}
        >
          <Alert variant="filled" severity="error">
            {`${errorModel?.name}`}
            <br />
            {getTranslation(translations, "pm.list.publish.error")}
          </Alert>
        </MUILink>
      </Snackbar>
    </Box>
  );
};

export default ProcessLibraryPage;
